import { USE_BACKEND } from '../config/env';
import type {
    HistoricalSnapshotListResponse,
    ListHistoricalSnapshotsParams,
    HistoricalSnapshotDetail,
    HistoricalReportListResponse,
    ListReportsRequest,
    HistoricalReportDetail as BackendHistoricalReportDetail
} from '../types';

// Legacy service interface for backward compatibility
export interface HistoricalReportService {
    listSnapshots(params: ListHistoricalSnapshotsParams): Promise<HistoricalSnapshotListResponse>;
    getSnapshot(id: string): Promise<HistoricalSnapshotDetail>;
}

// New backend service interface
export interface BackendHistoricalReportService {
    listReports(request: ListReportsRequest): Promise<HistoricalReportListResponse>;
    getReportDetail(reportId: string, userEmail: string, targetLanguage?: string): Promise<BackendHistoricalReportDetail>;
}

// Authenticated service interface (uses authManager)
export interface AuthenticatedHistoricalReportService {
    listReports(filters?: Omit<ListReportsRequest, 'user_email'>): Promise<HistoricalReportListResponse>;
    getReportDetail(reportId: string, targetLanguage?: string): Promise<BackendHistoricalReportDetail>;
}

let instancePromise: Promise<HistoricalReportService> | null = null;
let backendInstancePromise: Promise<BackendHistoricalReportService> | null = null;
let authenticatedInstancePromise: Promise<AuthenticatedHistoricalReportService> | null = null;

async function create(): Promise<HistoricalReportService> {
    if (USE_BACKEND) {
        const { apiHistoricalReportService } = await import('./historicalReportService.api');
        return apiHistoricalReportService as HistoricalReportService;
    } else {
        const { MockHistoricalReportService } = await import('./historicalReportService.mock');
        return new MockHistoricalReportService();
    }
}

async function createBackend(): Promise<BackendHistoricalReportService> {
    const { backendHistoricalReportService } = await import('./historicalReportService.backend');
    return backendHistoricalReportService;
}

async function createAuthenticated(): Promise<AuthenticatedHistoricalReportService> {
    const { authenticatedHistoricalReportService } = await import('./historicalReportService.backend');
    return authenticatedHistoricalReportService;
}

function getInstance(): Promise<HistoricalReportService> {
    if (!instancePromise) instancePromise = create();
    return instancePromise;
}

function getBackendInstance(): Promise<BackendHistoricalReportService> {
    if (!backendInstancePromise) backendInstancePromise = createBackend();
    return backendInstancePromise;
}

function getAuthenticatedInstance(): Promise<AuthenticatedHistoricalReportService> {
    if (!authenticatedInstancePromise) authenticatedInstancePromise = createAuthenticated();
    return authenticatedInstancePromise;
}

// Legacy service for backward compatibility
export const historicalReportService: HistoricalReportService = {
    async listSnapshots(params: ListHistoricalSnapshotsParams): Promise<HistoricalSnapshotListResponse> {
        const svc = await getInstance();
        return svc.listSnapshots(params);
    },
    async getSnapshot(id: string): Promise<HistoricalSnapshotDetail> {
        const svc = await getInstance();
        return svc.getSnapshot(id);
    }
};

// New backend service (requires explicit user email)
export const backendHistoricalReportService: BackendHistoricalReportService = {
    async listReports(request: ListReportsRequest): Promise<HistoricalReportListResponse> {
        const svc = await getBackendInstance();
        return svc.listReports(request);
    },
    async getReportDetail(reportId: string, userEmail: string, targetLanguage?: string): Promise<BackendHistoricalReportDetail> {
        const svc = await getBackendInstance();
        return svc.getReportDetail(reportId, userEmail, targetLanguage);
    }
};

// Authenticated service (uses authManager for automatic user email)
export const authenticatedHistoricalReportService: AuthenticatedHistoricalReportService = {
    async listReports(filters?: Omit<ListReportsRequest, 'user_email'>): Promise<HistoricalReportListResponse> {
        const svc = await getAuthenticatedInstance();
        return svc.listReports(filters);
    },
    async getReportDetail(reportId: string, targetLanguage?: string): Promise<BackendHistoricalReportDetail> {
        const svc = await getAuthenticatedInstance();
        return svc.getReportDetail(reportId, targetLanguage);
    }
};
